/**
 * Event Bindings Module for Fyncall Widget
 * Centralizes event handler setup for all widget components
 */

/**
 * Bind launcher events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindLauncherEvents(elements, callbacks) {
    if (elements.launcher) {
        elements.launcher.onclick = callbacks.onToggle;
    }
}

/**
 * Bind minimize/close button events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 * @param {boolean} isSDKMode - Whether in SDK mode
 */
export function bindMinimizeEvents(elements, callbacks, isSDKMode) {
    if (elements.minimizeBtn) {
        elements.minimizeBtn.onclick = () => {
            if (isSDKMode && window.FyncallApp && window.FyncallApp.requestClose) {
                window.FyncallApp.requestClose();
            } else {
                callbacks.onClose();
            }
        };
    }
}

/**
 * Auto-resize textarea to fit content (max 3 lines)
 * @param {HTMLTextAreaElement} textarea - The textarea element
 */
function autoResizeTextarea(textarea) {
    // Reset height to calculate new scrollHeight
    textarea.style.height = 'auto';
    // Set to scrollHeight but cap at max-height (72px ~3 lines)
    const maxHeight = 72;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
}

/**
 * Bind input area events (send button, input field, file attachment)
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindInputEvents(elements, callbacks) {
    if (elements.sendBtn) {
        elements.sendBtn.onclick = callbacks.onSend;
    }

    if (elements.input) {
        // Handle Enter (send) and Shift+Enter (new line)
        elements.input.onkeydown = (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                callbacks.onSend();
                // Reset height after sending
                elements.input.style.height = 'auto';
            }
            // Shift+Enter will naturally create new line in textarea
        };

        // Auto-resize on input
        elements.input.oninput = () => {
            autoResizeTextarea(elements.input);
        };
    }

    if (elements.attachBtn && elements.fileInput) {
        elements.attachBtn.onclick = () => elements.fileInput.click();
    }

    if (elements.fileInput) {
        elements.fileInput.onchange = callbacks.onFileSelect;
    }

    if (elements.fileRemove) {
        elements.fileRemove.onclick = callbacks.onFileClear;
    }
}

/**
 * Bind proactive engagement events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindProactiveEvents(elements, callbacks) {
    if (elements.proactiveClose) {
        elements.proactiveClose.onclick = callbacks.onHide;
    }

    if (elements.proactiveBtn) {
        elements.proactiveBtn.onclick = () => {
            callbacks.onHide();
            callbacks.onOpen();
        };
    }
}

/**
 * Bind cart panel events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindCartPanelEvents(elements, callbacks) {
    if (elements.headerCart) {
        elements.headerCart.onclick = callbacks.onOpenCart;
    }

    if (elements.cartBack) {
        elements.cartBack.onclick = callbacks.onCloseCart;
    }

    if (elements.checkoutBtn) {
        elements.checkoutBtn.onclick = callbacks.onCheckout;
    }

    // Promo code events
    if (elements.cartPromoToggle) {
        elements.cartPromoToggle.onclick = callbacks.onTogglePromo;
    }

    if (elements.cartPromoBtn) {
        elements.cartPromoBtn.onclick = callbacks.onApplyPromo;
    }

    if (elements.cartPromoInput) {
        elements.cartPromoInput.onkeypress = (e) => {
            if (e.key === 'Enter') callbacks.onApplyPromo();
        };
    }
}

/**
 * Bind variant modal events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindVariantModalEvents(elements, callbacks) {
    if (elements.variantModalClose) {
        elements.variantModalClose.onclick = callbacks.onClose;
    }

    if (elements.variantCancel) {
        elements.variantCancel.onclick = callbacks.onClose;
    }

    if (elements.variantAdd) {
        elements.variantAdd.onclick = callbacks.onAddToCart;
    }

    if (elements.variantModal) {
        elements.variantModal.onclick = (e) => {
            if (e.target === elements.variantModal) callbacks.onClose();
        };
    }
}

/**
 * Bind pre-chat form events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindPrechatFormEvents(elements, callbacks) {
    if (elements.prechatContinue) {
        elements.prechatContinue.onclick = callbacks.onContinue;
    }

    if (elements.prechatSkip) {
        elements.prechatSkip.onclick = callbacks.onSkip;
    }

    if (elements.prechatEmail) {
        elements.prechatEmail.onkeypress = (e) => {
            if (e.key === 'Enter') callbacks.onContinue();
        };
    }
}

/**
 * Bind CSAT survey events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindCsatEvents(elements, callbacks) {
    if (elements.csatSubmit) {
        elements.csatSubmit.onclick = callbacks.onSubmit;
    }

    if (elements.csatSkip) {
        elements.csatSkip.onclick = callbacks.onClose;
    }

    // Star rating clicks
    if (elements.csatStars) {
        elements.csatStars.querySelectorAll('.fyncall-csat-star').forEach(star => {
            star.onclick = () => callbacks.onStarRating(parseInt(star.dataset.rating));
        });
    }

    // Thumbs rating clicks
    if (elements.csatThumbs) {
        elements.csatThumbs.querySelectorAll('.fyncall-csat-thumb').forEach(thumb => {
            thumb.onclick = () => callbacks.onThumbsRating(thumb.dataset.rating);
        });
    }

    // NPS rating clicks
    if (elements.csatNps) {
        elements.csatNps.querySelectorAll('.fyncall-csat-nps-btn').forEach(btn => {
            btn.onclick = () => callbacks.onNpsRating(parseInt(btn.dataset.rating));
        });
    }
}

/**
 * Bind header menu events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindHeaderMenuEvents(elements, callbacks) {
    if (elements.menuBtn && elements.menuDropdown) {
        elements.menuBtn.onclick = (e) => {
            e.stopPropagation();
            callbacks.onToggleMenu();
        };

        // Close menu when clicking outside
        document.addEventListener('click', (e) => {
            if (elements.menuDropdown &&
                elements.menuDropdown.classList.contains('show') &&
                !elements.menuDropdown.contains(e.target) &&
                !elements.menuBtn.contains(e.target)) {
                callbacks.onCloseMenu();
            }
        });
    }

    // Sound toggle
    const soundToggle = document.getElementById('fyncall-sound-toggle');
    if (soundToggle) {
        soundToggle.onclick = () => {
            callbacks.onToggleSound();
            callbacks.onCloseMenu();
        };
    }

    // Dark mode toggle
    const darkModeToggle = document.getElementById('fyncall-darkmode-toggle');
    if (darkModeToggle) {
        darkModeToggle.onclick = () => {
            callbacks.onToggleDarkMode();
            callbacks.onCloseMenu();
        };
    }

    // End conversation button
    if (elements.endConversationBtn) {
        elements.endConversationBtn.onclick = () => {
            callbacks.onCloseMenu();
            callbacks.onEndConversation();
        };
    }
}

/**
 * Bind end chat modal events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindEndChatModalEvents(elements, callbacks) {
    if (elements.endChatDownload) {
        elements.endChatDownload.onclick = () => {
            callbacks.onDownload();
            callbacks.onCloseModal();
            callbacks.onFinalize();
        };
    }

    if (elements.endChatClose) {
        elements.endChatClose.onclick = () => {
            callbacks.onCloseModal();
            callbacks.onFinalize();
        };
    }

    if (elements.endChatModal) {
        elements.endChatModal.onclick = (e) => {
            if (e.target === elements.endChatModal) {
                callbacks.onCloseModal();
                callbacks.onFinalize();
            }
        };
    }
}

/**
 * Bind home screen events
 * @param {Object} elements - Widget elements
 * @param {Object} callbacks - Event callbacks
 */
export function bindHomeScreenEvents(elements, callbacks) {
    if (elements.homeClose) {
        elements.homeClose.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            callbacks.onClose();
        });
    }

    if (elements.chatBack) {
        elements.chatBack.onclick = callbacks.onShowHome;
    }

    if (elements.startChat) {
        elements.startChat.onclick = () => callbacks.onShowChat();
    }

    if (elements.startChatBtn) {
        elements.startChatBtn.onclick = () => callbacks.onShowChat();
    }

    // Home search input
    if (elements.homeSearchInput) {
        elements.homeSearchInput.onkeypress = (e) => {
            if (e.key === 'Enter' && elements.homeSearchInput.value.trim()) {
                const query = elements.homeSearchInput.value.trim();
                elements.homeSearchInput.value = '';
                callbacks.onShowChat(query);
            }
        };
    }

    // Starter send button
    if (elements.starterSend) {
        elements.starterSend.onclick = () => {
            if (elements.homeSearchInput && elements.homeSearchInput.value.trim()) {
                const query = elements.homeSearchInput.value.trim();
                elements.homeSearchInput.value = '';
                callbacks.onShowChat(query);
            } else {
                callbacks.onShowChat();
            }
        };
    }
}

/**
 * Bind activity tracking events
 * @param {Function} onActivity - Activity callback
 */
export function bindActivityTracking(onActivity) {
    document.addEventListener('mousemove', onActivity);
    document.addEventListener('keypress', onActivity);
}

/**
 * Bind all widget events
 * @param {Object} elements - Widget elements
 * @param {Object} widget - Widget instance with method callbacks
 * @param {boolean} isSDKMode - Whether in SDK mode
 */
export function bindAllEvents(elements, widget, isSDKMode) {
    // Only bind launcher if not in SDK mode
    if (!isSDKMode) {
        bindLauncherEvents(elements, { onToggle: () => widget.toggle() });
    }

    bindMinimizeEvents(elements, { onClose: () => widget.close() }, isSDKMode);

    bindInputEvents(elements, {
        onSend: () => widget.sendMessage(),
        onFileSelect: (e) => widget.handleFileSelect(e),
        onFileClear: () => widget.clearFilePreview()
    });

    bindProactiveEvents(elements, {
        onHide: () => widget.hideProactive(),
        onOpen: () => widget.open()
    });

    bindCartPanelEvents(elements, {
        onOpenCart: () => widget.openCartPanel(),
        onCloseCart: () => widget.closeCartPanel(),
        onCheckout: () => widget.handleCheckout(),
        onTogglePromo: () => widget.togglePromoForm(),
        onApplyPromo: () => widget.applyPromoCode()
    });

    bindVariantModalEvents(elements, {
        onClose: () => widget.closeVariantModal(),
        onAddToCart: () => widget.handleVariantAddToCart()
    });

    bindPrechatFormEvents(elements, {
        onContinue: () => widget.handlePrechatContinue(),
        onSkip: () => widget.handlePrechatSkip()
    });

    bindCsatEvents(elements, {
        onSubmit: () => widget.submitCsatSurvey(),
        onClose: () => widget.closeCsatSurvey(),
        onStarRating: (rating) => widget.handleStarRating(rating),
        onThumbsRating: (rating) => widget.handleThumbsRating(rating),
        onNpsRating: (rating) => widget.handleNpsRating(rating)
    });

    bindHeaderMenuEvents(elements, {
        onToggleMenu: () => widget.toggleHeaderMenu(),
        onCloseMenu: () => widget.closeHeaderMenu(),
        onToggleSound: () => widget.toggleSound(),
        onToggleDarkMode: () => widget.toggleDarkMode(),
        onEndConversation: () => widget.endConversation()
    });

    bindEndChatModalEvents(elements, {
        onDownload: () => widget.downloadTranscriptText(),
        onCloseModal: () => widget.closeEndChatModal(),
        onFinalize: () => widget.finalizeEndConversation()
    });

    bindHomeScreenEvents(elements, {
        onClose: () => widget.close(),
        onShowHome: () => widget.showHomeScreen(),
        onShowChat: (query) => widget.showChatView(query)
    });

    // New chat button
    if (elements.newChatBtn) {
        elements.newChatBtn.onclick = () => widget.confirmNewSession();
    }

    // Activity tracking
    bindActivityTracking(() => widget.lastActivity = Date.now());
}
